import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Stack, Button, TextField, Select, MenuItem, FormControl,
  InputLabel, Switch, FormControlLabel, IconButton, Skeleton, CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Folder as FolderIcon, Refresh as RefreshIcon, Save as SaveIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import api from '../api/axios';
import { color, radius } from '../theme/tokens';
import {
  PageHeader, Panel, StatusBadge, EmptyState, ConfirmDialog, FormDialog, Toolbar, useFeedback,
} from '../components/ui';

const emptyCategory = () => ({
  name: '', slug: '', parent_id: '', image_url: '', is_active: 1, sort_order: 0,
});

const CategoryDialog = ({ open, onClose, category, categories, onSaved }) => {
  const { success, error } = useFeedback();
  const [form, setForm] = useState(emptyCategory());
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const isEdit = !!category?.id;

  useEffect(() => {
    if (!open) return;
    setForm(category ? {
      name: category.name || '',
      slug: category.slug || '',
      parent_id: category.parent_id || '',
      image_url: category.image_url || '',
      is_active: category.is_active ?? 1,
      sort_order: category.sort_order || 0,
    } : emptyCategory());
    setErr('');
  }, [open, category]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name) { setErr('Name is required'); return; }
    setSaving(true); setErr('');
    try {
      const payload = { ...form, parent_id: form.parent_id || null };
      if (isEdit) {
        await api.put(`/api/v2/categories/${category.id}`, payload);
        success('Category updated');
      } else {
        await api.post('/api/v2/categories', payload);
        success('Category created');
      }
      onSaved();
    } catch (e) {
      error(e.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit category' : 'New category'}
      subtitle={isEdit ? 'Update the category details' : 'Add a category to the catalogue'}
      icon={<FolderIcon sx={{ fontSize: 18 }} />}
      error={err}
      saving={saving}
      actions={
        <>
          <Button onClick={onClose} disabled={saving} variant="outlined" color="inherit">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} variant="contained" startIcon={<SaveIcon />}>
            {saving ? <CircularProgress size={18} color="inherit" /> : (isEdit ? 'Update' : 'Create')}
          </Button>
        </>
      }
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        <TextField
          fullWidth size="small" label="Name" required
          value={form.name} onChange={e => set('name', e.target.value)}
        />
        <TextField
          fullWidth size="small" label="Slug (auto-generated if empty)"
          value={form.slug} onChange={e => set('slug', e.target.value)}
        />
        <FormControl fullWidth size="small">
          <InputLabel>Parent category</InputLabel>
          <Select
            value={form.parent_id} label="Parent category"
            onChange={e => set('parent_id', e.target.value)}
          >
            <MenuItem value=""><em>Root (no parent)</em></MenuItem>
            {categories
              .filter(c => c.id !== category?.id)
              .map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField
          fullWidth size="small" label="Image URL"
          value={form.image_url} onChange={e => set('image_url', e.target.value)}
        />
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small" type="number" label="Sort order" sx={{ flex: 1 }}
            value={form.sort_order} onChange={e => set('sort_order', parseInt(e.target.value) || 0)}
          />
          <FormControlLabel
            control={<Switch checked={form.is_active === 1} onChange={e => set('is_active', e.target.checked ? 1 : 0)} />}
            label="Active"
          />
        </Box>
      </Stack>
    </FormDialog>
  );
};

const CategoryNode = ({ cat, categories, onEdit, onDelete, visibleIds, depth = 0 }) => {
  const children = categories.filter(
    c => c.parent_id === cat.id && (!visibleIds || visibleIds.has(c.id))
  );
  const hasChildren = children.length > 0;

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 1,
          pl: 1.5,
          pr: 1,
          borderBottom: `1px solid ${color.divider}`,
          transition: 'background-color .12s ease',
          '&:hover': { bgcolor: color.ink[50] },
        }}
      >
        {Array.from({ length: depth }).map((_, i) => (
          <Box
            key={i}
            sx={{ width: 24, alignSelf: 'stretch', borderLeft: `1px solid ${color.divider}`, flexShrink: 0 }}
          />
        ))}

        <Box sx={{ width: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: color.ink[400] }}>
          {hasChildren && <ChevronRightIcon sx={{ fontSize: 16 }} />}
        </Box>

        <Box
          sx={{
            width: 28, height: 28, borderRadius: `${radius.md}px`, flexShrink: 0,
            bgcolor: color.primary[50], color: color.primary[600],
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <FolderIcon sx={{ fontSize: 16 }} />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: color.ink[900] }}>
            {cat.name}
          </Typography>
          {cat.slug && (
            <StatusBadge tone="neutral" dot={false} label={cat.slug} sx={{ fontFamily: 'monospace' }} />
          )}
          <Typography sx={{ fontSize: '0.75rem', color: color.ink[500] }}>
            {children.length} {children.length === 1 ? 'child' : 'children'}
          </Typography>
          {!cat.is_active && <StatusBadge tone="neutral" label="Inactive" />}
        </Box>

        <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
          <IconButton size="small" onClick={() => onEdit(cat)} aria-label={`Edit ${cat.name}`}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small" onClick={() => onDelete(cat)} aria-label={`Delete ${cat.name}`}
            sx={{ color: color.danger.text, '&:hover': { bgcolor: color.danger.bg, color: color.danger.text } }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      {children.map(child => (
        <CategoryNode
          key={child.id} cat={child} categories={categories}
          onEdit={onEdit} onDelete={onDelete} visibleIds={visibleIds} depth={depth + 1}
        />
      ))}
    </>
  );
};

const TreeSkeleton = () => (
  <Box sx={{ p: 1.5 }}>
    {[0, 1, 2, 3, 4].map(i => (
      <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.25, pl: 1 + (i % 2) * 3 }}>
        <Skeleton variant="rounded" width={28} height={28} />
        <Skeleton variant="text" width={`${38 + i * 7}%`} height={22} />
        <Skeleton variant="rounded" width={56} height={20} sx={{ ml: 'auto' }} />
      </Box>
    ))}
  </Box>
);

const CategoriesV2 = () => {
  const { success, error } = useFeedback();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editCat, setEditCat] = useState(null);
  const [confirmCat, setConfirmCat] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [inputVal, setInputVal] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/v2/categories/flat');
      setCategories(res.data.data || []);
    } catch (e) {
      error(e.response?.data?.message || 'Failed to load categories');
    } finally { setLoading(false); }
  }, [error]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  // Debounce the search box
  useEffect(() => {
    const t = setTimeout(() => setSearch(inputVal), 300);
    return () => clearTimeout(t);
  }, [inputVal]);

  const handleNew = () => { setEditCat(null); setDialogOpen(true); };
  const handleEdit = (c) => { setEditCat(c); setDialogOpen(true); };

  const confirmDelete = async () => {
    if (!confirmCat) return;
    try {
      setDeleting(true);
      await api.delete(`/api/v2/categories/${confirmCat.id}`);
      await fetchCategories();
      success('Category deleted');
    } catch (e) {
      error(e.response?.data?.message || 'Failed to delete category');
    } finally {
      setDeleting(false);
      setConfirmCat(null);
    }
  };

  const filtering = Boolean(search.trim()) || statusFilter !== 'all';

  // Keep the tree intact: a node is visible if it matches, or if any of its
  // descendants match (ancestors are added so the hierarchy stays readable).
  const visibleIds = useMemo(() => {
    if (!filtering) return null;
    const byId = Object.fromEntries(categories.map(c => [c.id, c]));
    const q = search.trim().toLowerCase();
    const set = new Set();
    categories.forEach(c => {
      const statusOk = statusFilter === 'all' || (statusFilter === 'active' ? !!c.is_active : !c.is_active);
      const textOk = !q || (c.name || '').toLowerCase().includes(q) || (c.slug || '').toLowerCase().includes(q);
      if (statusOk && textOk) {
        set.add(c.id);
        let p = c.parent_id;
        while (p && byId[p]) { set.add(p); p = byId[p].parent_id; }
      }
    });
    return set;
  }, [categories, search, statusFilter, filtering]);

  const roots = categories.filter(c => !c.parent_id && (!visibleIds || visibleIds.has(c.id)));

  return (
    <Box>
      <PageHeader
        eyebrow="Catalog"
        title="Categories"
        subtitle={filtering
          ? `${roots.length} of ${categories.filter(c => !c.parent_id).length} top-level categories shown`
          : `${categories.length} categories`}
        actions={
          <>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchCategories} disabled={loading}>
              Refresh
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleNew}>
              Add category
            </Button>
          </>
        }
      />

      <Box sx={{ mb: 2 }}>
        <Toolbar
          search={inputVal}
          onSearch={setInputVal}
          searchPlaceholder="Search by name or slug..."
          onRefresh={fetchCategories}
          refreshing={loading}
        >
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="cat-status-label">Status</InputLabel>
            <Select
              labelId="cat-status-label"
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All statuses</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Toolbar>
      </Box>

      <Panel
        title="Category tree"
        subtitle="Parents and their nested children"
        noPadding
        bodySx={{ '& > div:last-child': { borderBottom: 'none' } }}
      >
        {loading ? (
          <TreeSkeleton />
        ) : categories.length === 0 ? (
          <EmptyState
            icon={<FolderIcon sx={{ fontSize: 22 }} />}
            title="No categories yet"
            description="Create your first category to start organising the catalogue."
            actionLabel="Add category"
            onAction={handleNew}
          />
        ) : roots.length === 0 ? (
          <EmptyState
            icon={<FolderIcon sx={{ fontSize: 22 }} />}
            title="No matching categories"
            description="Try a different search term or status filter."
          />
        ) : (
          roots.map(c => (
            <CategoryNode
              key={c.id} cat={c} categories={categories}
              onEdit={handleEdit} onDelete={setConfirmCat} visibleIds={visibleIds}
            />
          ))
        )}
      </Panel>

      <CategoryDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        category={editCat}
        categories={categories}
        onSaved={() => { setDialogOpen(false); fetchCategories(); }}
      />

      <ConfirmDialog
        open={!!confirmCat}
        onClose={() => setConfirmCat(null)}
        onConfirm={confirmDelete}
        title="Delete category"
        description={confirmCat ? `Delete category "${confirmCat.name}"? This action cannot be undone.` : ''}
        confirmLabel="Delete"
        tone="danger"
        loading={deleting}
      />
    </Box>
  );
};

export default CategoriesV2;
